import { useSwitchChain } from '@wagmi/vue';
import { storeToRefs } from 'pinia';
import { useCartStore, useProviderConfig } from '#imports';
import { useAlertsStore } from '~/stores/alerts/useAlertsStore';
import { useTransactionStore } from '~/stores/transaction/useTransactionStore';
import { useUserStore } from '~/stores/user/useUserStore';
import useEVMApi from '~/composables/useApi/useEVMApi';
import type { ValidateOrderWithUuid } from '~/service/utils/validator/order/types';
import type { EvmEventMap } from '~/service/utils/eventEmitter';
import useEVMCore from '~/composables/web3/EVM/useEVMCore';

export default function useBuyNft() {
    const userStore = useUserStore();
    const alertStore = useAlertsStore();
    const evmCore = useEVMCore();
    const cartStore = useCartStore();
    const { cart } = storeToRefs(cartStore);

    const cartValues = computed(() => [...cart.value.values()]);

    const { map: { chainMap } } = useProviderConfig()(['map', 'contract']);

    const {
        order
    } = useEVMApi();

    const { chainId, user } = storeToRefs(userStore);

    const { addTransaction } = useTransactionStore();

    const { switchChainAsync } = useSwitchChain();

    const { $provider } = useNuxtApp();

    const { eventEmitter } = useEVMCore();

    const buyNFT = async ({ items, transactionType = 'purchase:buy_now', initiatorKey }: { items?: AppCartItemType[]; transactionType?: Extract<EvmEventMap['type'], `purchase:${string}`>; initiatorKey?: string } = {}) => {
        if (!user.value) return userStore.showUserIsDisconnect();

        const orderValidator = evmCore.createEvmOrderValidator($provider);

        const paymentValues = items?.length
            ? items
            : cartValues.value;

        const paymentNetwork = paymentValues[0]?.network;

        const paymentUuids = paymentValues.map(item => item.uuid);

        return await eventEmitter.emit(transactionType, async () => {
            const { data: responseOrders } = await order.bestOrders(paymentUuids);

            const { withError, withoutError } = orderValidator.partitionOrders(responseOrders, paymentValues);

            const flattenedOrderList = Object.values(withoutError).flat();

            const withErrorOrders: ValidateOrderWithUuid[] = withError.map(item => ({ status: 'expired', uuid: item.uuid, message: 'asd' }));

            const validatedBestOrders = await orderValidator.validateBestOrder({
                items: paymentValues,
                type: transactionType,
                orderList: flattenedOrderList
            });

            const validatedUuids = new Set(validatedBestOrders.map(item => item.uuid));

            const filteredWithErrorOrders = withErrorOrders.filter(
                item => !validatedUuids.has(item.uuid)
            );

            for (const validatedBest of [...validatedBestOrders, ...filteredWithErrorOrders]) {
                if (validatedBest?.status !== 'idle' && validatedBest?.message) {
                    alertStore.add({
                        title: 'Oops, something went wrong',
                        description: validatedBest?.message,
                        critical: false,
                        status: 'type.info'
                    });
                }

                if (validatedBest?.status === 'expired') {
                    cartStore.changeCartItem(validatedBest.uuid, { status: 'expired' });
                }

                else if (validatedBest?.status === 'return_best' && validatedBest.order.price?.amount) {
                    cartStore.changeCartItem(validatedBest.uuid, {
                        price: {
                            ...validatedBest.order.price,
                            amount: validatedBest.order.price?.amount
                        },
                        maxAmount: validatedBest.order.maxAmount
                    });
                }
            }

            if (withoutError) {
                const protocolOrders = Object.entries(withoutError);

                if (protocolOrders.length && paymentNetwork && chainMap[paymentNetwork]) {
                    const nftBuyer = evmCore.createEvmNFTBuyer({
                        switchNetworkFn: switchChainAsync,
                        user: {
                            address: user.value,
                            chainId: chainId.value
                        },
                        ...$provider
                    });

                    const paymentChain = chainMap[paymentNetwork];

                    const transactions = await nftBuyer.processBuyTransactions({
                        protocolOrders,
                        chainId: paymentChain.id,
                        nativeCurrencySymbol: paymentChain.nativeCurrency.symbol
                    });

                    if (transactions) {
                        transactions.forEach((tx) => {
                            addTransaction({
                                hash: tx.value,
                                chainId: paymentChain.id,
                                tokenList: paymentValues,
                                value: tx.sum,
                                currency: paymentNetwork,
                                status: 'pending',
                                type: transactionType
                            });
                        });
                    }
                    return transactions;
                }
            }
        }, undefined, initiatorKey);
    };

    return {
        buyNFT
    };
}
